import { toast } from "@birdwatch/ui/toast";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "cn";
import {
  type ComponentProps,
  type JSX,
  type MouseEvent,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { formatPhoneWithExtension } from "@/libs/format-phone-number";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";

/** `Tooltip` self-provides a Provider hardcoded to `delayDuration={0}`, so the delay lives here. */
const HOVER_DELAY_MS = 250;
/** How long "Copied!" survives after the pointer leaves. */
const RESET_DELAY_MS = 500;

/** The only clipboard string-write in webapp/src. Reports its own failure so no caller has to. */
async function writeClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    toast.error({ title: "Couldn't copy to clipboard" });
    return false;
  }
}

/**
 * Copy from an imperative handler — a `DropdownMenuItem` `onSelect`, an action-config
 * `onClick`. Toasts because the surface closes on select and has nowhere left to show
 * inline feedback.
 *
 * Prefer `CopyTooltip` for anything that stays on screen. Never nest a tooltip inside a
 * Radix menu: `TooltipContent` portals outside the menu's `DismissableLayer` and can stay
 * painted after the menu closes, and the trigger's pointer handlers fight the menu's own
 * `data-highlighted` tracking. A menu item is also selectable by keyboard, which a nested
 * `onClick` would never see — which is why menu items call this instead of `CopyToast`.
 */
export function copyToast(
  text: string,
  toastContent = "Copied to clipboard!",
): void {
  void writeClipboard(text).then((ok) => {
    if (ok) toast.success({ title: toastContent });
  });
}

export type CopyTooltipProps = PropsWithChildren & {
  copy: string;
  /** Idle tooltip text. Always swaps to "Copied!" on success. Default "Click to copy". */
  tooltipContent?: string;
  side?: ComponentProps<typeof TooltipContent>["side"];
  /**
   * Render the single child as the click target and tooltip trigger instead of wrapping it
   * in a div — keeps `truncate` / flex layouts intact. The child must be a DOM element or a
   * ref-forwarding component, and must NOT set its own `onClick`: Slot *composes* handlers,
   * so both would run and TypeScript would not complain.
   */
  asChild?: boolean;
  /** Keep the click off a clickable ancestor — a table row with a row-click handler. */
  stopPropagation?: boolean;
  className?: string;
};

/** The default copy affordance: click the content, the tooltip says "Copied!". No toast. */
export function CopyTooltip({
  copy,
  tooltipContent,
  side,
  asChild = false,
  stopPropagation = false,
  className,
  children,
}: CopyTooltipProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const [hoverOpen, setHoverOpen] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current === null) return;
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = null;
  }, []);

  useEffect(() => clearHoverTimer, [clearHoverTimer]);

  // `copied` belongs in the deps: a tap that never opened the tooltip (touch, or a click
  // inside the 250ms delay) leaves `hoverOpen` false, so without it this effect never
  // re-runs and the next hover opens on a stale "Copied!".
  useEffect(() => {
    if (!copied || hoverOpen) return;
    const timer = setTimeout(() => setCopied(false), RESET_DELAY_MS);
    return () => clearTimeout(timer);
  }, [copied, hoverOpen]);

  const handleClick = useCallback(
    (event: MouseEvent) => {
      if (stopPropagation) event.stopPropagation();
      void writeClipboard(copy).then(setCopied);
    },
    [copy, stopPropagation],
  );

  const handlePointerEnter = useCallback(() => {
    clearHoverTimer();
    hoverTimerRef.current = setTimeout(
      () => setHoverOpen(true),
      HOVER_DELAY_MS,
    );
  }, [clearHoverTimer]);

  const handlePointerLeave = useCallback(() => {
    clearHoverTimer();
    setHoverOpen(false);
  }, [clearHoverTimer]);

  const Comp = asChild ? Slot : "div";

  return (
    <Tooltip open={hoverOpen} disableHoverableContent>
      <TooltipTrigger asChild>
        <Comp
          onClick={handleClick}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
          className={cn("cursor-pointer", className)}
        >
          {children}
        </Comp>
      </TooltipTrigger>
      <TooltipContent side={side}>
        {copied ? "Copied!" : (tooltipContent ?? "Click to copy")}
      </TooltipContent>
    </Tooltip>
  );
}

export type CopyToastProps = PropsWithChildren & {
  copy: string;
  /** Toast title on success. Default "Copied to clipboard!". */
  toastContent?: string;
  className?: string;
};

/** Copy affordance for surfaces with no room to hover a tooltip. Toasts instead. */
export function CopyToast({
  copy,
  toastContent,
  className,
  children,
}: CopyToastProps): JSX.Element {
  const handleClick = useCallback(
    () => copyToast(copy, toastContent),
    [copy, toastContent],
  );

  return (
    <div onClick={handleClick} className={cn("cursor-pointer", className)}>
      {children}
    </div>
  );
}

export type CopyPhoneNumberProps = {
  phone: string | null | undefined;
  extension?: string | null;
  className?: string;
};

/** A phone number is copied in the form it is displayed — one home for that decision. */
export function CopyPhoneNumber({
  phone,
  extension,
  className,
}: CopyPhoneNumberProps): JSX.Element | null {
  if (!phone) return null;
  const display = formatPhoneWithExtension(phone, extension);

  return (
    <CopyTooltip
      copy={display}
      className={cn(
        /* sorted */
        "line-clamp-1",
        "text-nowrap break-all",
        className,
      )}
    >
      {display}
    </CopyTooltip>
  );
}
